import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { apiFetch } from '../../api';
import { formatFeatureLabel, getFeatureDefinition } from '../../featureLabels';
import { useCenters } from '../../hooks/useCenters';
import { usePatients } from '../../hooks/usePatients';
import { usePatientZScores } from '../context/PatientZScoresContext';
import { useNormativeFeatures, useNormativeRegions } from '../hooks/useNormativeData';
import { MultiSelectDropdown } from './MultiSelectDropdown';


export const NormativePanel = ({ patientDataApi }) => {
    // State for patient selection and features
    const [selectedCenters, setSelectedCenters] = useState([]);
    const [selectedPatients, setSelectedPatients] = useState([]);
    const { features, loading: featuresLoading, error: featuresError } = useNormativeFeatures();
    const { regions, loading: regionsLoading, error: regionsError } = useNormativeRegions();
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [selectedROIs, setSelectedROIs] = useState([]);
    const montageToggleRef = useRef(null);
    const [harmonize, setHarmonize] = useState(true);
    const harmonizeToggleRef = useRef(null);
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Use the patient z-scores context instead of local state
    const { patientZScores, zScoreVisualizationSettings, montageType, setMontageType } = usePatientZScores();

    // Use the hooks for centers and patients
    const { centers, loading: centersLoading, error: centersError } = useCenters();
    const { patients, loading: patientsLoading, error: patientsError } = usePatients();

    const noop = () => { };
    const {
        fetchAndPlotPatientsBatch = noop,
        clearPatientData = noop,
        updatePatientVisualization = noop,
        patientDataError = null,
        viewerReady = false
    } = patientDataApi || {};

    // This function handles selecting/deselecting a center (site)
    const handleCenterToggle = useCallback((centerId) => {
        setSelectedCenters(prev => {
            const isSelected = prev.includes(centerId);
            const updatedCenters = isSelected
                ? prev.filter(id => id !== centerId)
                : [...prev, centerId];

            // Find the center object to get its patients
            const centerObj = centers.find(c => c.id === centerId);
            if (centerObj && centerObj.patients) {
                if (!isSelected) {
                    // If center is being selected, add all its patients that aren't already selected
                    setSelectedPatients(prevPatients => {
                        const patientsToAdd = centerObj.patients.filter(
                            patientId => !prevPatients.includes(patientId)
                        );

                        if (viewerReady && patientsToAdd.length > 0) {
                            fetchAndPlotPatientsBatch(patientsToAdd, zScoreVisualizationSettings, patientZScores);
                        }

                        return [...prevPatients, ...patientsToAdd];
                    });
                } else {
                    // If center is being deselected, remove all its patients
                    setSelectedPatients(prevPatients => {
                        // Clear data for removed patients if viewer is available
                        if (viewerReady) {
                            centerObj.patients.forEach(patientId => {
                                if (prevPatients.includes(patientId)) {
                                    clearPatientData(patientId);
                                }
                            });
                        }

                        return prevPatients.filter(
                            patientId => !centerObj.patients.includes(patientId)
                        );
                    });
                }
            }

            return updatedCenters;
        });
    }, [centers, viewerReady, fetchAndPlotPatientsBatch, clearPatientData, zScoreVisualizationSettings, patientZScores]);

    // Handle toggling an individual patient
    const handlePatientToggle = useCallback((patientId) => {
        setSelectedPatients(prev => {
            const isSelected = prev.includes(patientId);
            if (isSelected) {
                // Remove patient data from view if viewer is available
                if (viewerReady) {
                    clearPatientData(patientId);
                }
                return prev.filter(id => id !== patientId);
            } else {
                if (viewerReady) {
                    fetchAndPlotPatientsBatch([patientId], zScoreVisualizationSettings, patientZScores);
                }
                return [...prev, patientId];
            }
        });
    }, [viewerReady, fetchAndPlotPatientsBatch, clearPatientData, zScoreVisualizationSettings, patientZScores]);

    // Connect to the viewer whenever it becomes available
    useEffect(() => {
        // Check if global viewer exists and we need to plot patients
        if (viewerReady && selectedPatients.length > 0) {
            fetchAndPlotPatientsBatch(selectedPatients, zScoreVisualizationSettings, patientZScores);
        }
    }, [viewerReady, selectedPatients, fetchAndPlotPatientsBatch, zScoreVisualizationSettings, patientZScores]);

    // Update patient visualizations when z-score settings change
    useEffect(() => {
        if (viewerReady && selectedPatients.length > 0 && updatePatientVisualization) {
            updatePatientVisualization(selectedPatients, zScoreVisualizationSettings, patientZScores);
        }
    }, [zScoreVisualizationSettings, patientZScores, selectedPatients, viewerReady, updatePatientVisualization]);

    // Log patient data errors to console
    useEffect(() => {
        if (patientDataError && Object.keys(patientDataError).length > 0) {
            console.log('Patient data loading errors:');
            Object.entries(patientDataError).forEach(([patientId, error]) => {
                // Only log actual errors, not null values
                if (error !== null) {
                    console.log(`${patientId}: ${error}`);
                }
            });
        }
    }, [patientDataError]);

    const handleFeatureToggle = (featureId) => {
        setSelectedFeatures(prev => {
            if (prev.includes(featureId)) {
                return prev.filter(id => id !== featureId);
            } else {
                return [...prev, featureId];
            }
        });
    };

    const handleROIToggle = (roiId) => {
        setSelectedROIs(prev => {
            if (prev.includes(roiId)) {
                return prev.filter(id => id !== roiId);
            } else {
                return [...prev, roiId];
            }
        });
    };

    const downloadJsonFile = (payload, filename) => {
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const fetchJsonForDownload = async (path) => {
        const response = await apiFetch(path, { timeoutMs: 60000 });
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
            throw new Error(`Expected JSON from ${path}`);
        }
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || data.error || `HTTP error ${response.status} from ${path}`);
        }
        return data;
    };

    const handleDownloadNormativeData = async () => {
        if (selectedPatients.length === 0) {
            alert('Select at least one patient before downloading normative modeling data.');
            return;
        }
        if (selectedFeatures.length === 0) {
            alert('Select at least one feature before downloading normative modeling data.');
            return;
        }

        setDownloadLoading(true);
        try {
            const selectedRegionSet = new Set(selectedROIs);
            const selectedFeatureSet = new Set(selectedFeatures);
            const selectedFeatureMetadata = features
                .filter(feature => selectedFeatureSet.has(feature.id))
                .map(feature => ({
                    id: feature.id,
                    label: feature.label || formatFeatureLabel(feature.id),
                    description: feature.description || getFeatureDefinition(feature.id)?.description || null
                }));
            const selectedRegionMetadata = regions
                .filter(region => selectedRegionSet.has(region.id))
                .map(region => ({
                    id: region.id,
                    label: region.label || region.name || region.id
                }));

            const modelResponses = await Promise.all(selectedFeatures.map(featureId =>
                fetchJsonForDownload(`/api/normative-models?feature=${encodeURIComponent(featureId)}&montage=${encodeURIComponent(montageType)}`)
            ));
            const normativeModels = modelResponses
                .flat()
                .filter(model => (
                    selectedRegionSet.size === 0 ||
                    selectedRegionSet.has(model.region_name) ||
                    selectedRegionSet.has(model.region_id)
                ));

            const modelKeys = normativeModels.map(model => ({
                region: model.region_name,
                feature: model.feature_name
            }));

            const populationRows = await Promise.all(modelKeys.map(async ({ region, feature }) => {
                const query = `region=${encodeURIComponent(region)}&feature=${encodeURIComponent(feature)}&montage=${encodeURIComponent(montageType)}`;
                const [channelValues, zScores] = await Promise.all([
                    fetchJsonForDownload(`/api/channel-values?${query}`).catch(error => ({
                        error: error.message,
                        rows: []
                    })),
                    fetchJsonForDownload(`/api/z-scores?${query}`).catch(error => ({
                        error: error.message,
                        rows: []
                    }))
                ]);
                return {
                    region,
                    feature,
                    channel_values: Array.isArray(channelValues) ? channelValues : [],
                    z_scores: Array.isArray(zScores) ? zScores : [],
                    channel_values_error: channelValues?.error || null,
                    z_scores_error: zScores?.error || null
                };
            }));

            let selectedPatientZScores = [];
            let patientZScoreError = null;
            try {
                selectedPatientZScores = await fetchJsonForDownload(
                    `/api/patient-z-scores?patient_ids=${encodeURIComponent(selectedPatients.join(','))}&feature_ids=${encodeURIComponent(selectedFeatures.join(','))}&montage=${encodeURIComponent(montageType)}`
                );
            } catch (error) {
                patientZScoreError = error.message;
            }

            const payload = {
                generated_at: new Date().toISOString(),
                export_type: 'neuronova_normative_modeling',
                selections: {
                    patients: selectedPatients,
                    centers: selectedCenters,
                    features: selectedFeatureMetadata,
                    rois: selectedRegionMetadata,
                    montage: montageType,
                    harmonize
                },
                counts: {
                    normative_models: normativeModels.length,
                    population_groups: populationRows.length,
                    patient_z_score_patients: Array.isArray(selectedPatientZScores) ? selectedPatientZScores.length : 0
                },
                normative_models: normativeModels,
                population_distributions: populationRows,
                patient_z_scores: selectedPatientZScores,
                patient_z_scores_error: patientZScoreError
            };

            const featureLabel = selectedFeatures.length === 1 ? selectedFeatures[0] : `${selectedFeatures.length}-features`;
            downloadJsonFile(payload, `neuronova-normative-modeling-${featureLabel}-${Date.now()}.json`);
        } catch (error) {
            console.error('Failed to download normative modeling data:', error);
            alert(`Unable to download normative modeling data: ${error.message || 'Please try again.'}`);
        } finally {
            setDownloadLoading(false);
        }
    };

    // Get all patients as a flat list, but with site info for grouping
    const patientsWithSiteInfo = useMemo(() => {
        return patients.map(patient => {
            const center = centers.find(c => c.patients && c.patients.includes(patient.id));
            return {
                ...patient,
                siteId: center?.id || 'unknown',
                siteName: center?.label || 'Unknown Site'
            };
        });
    }, [patients, centers]);

    return (
        <>
            <section className="control-section">
                <h3>Select Sites</h3>
                <MultiSelectDropdown
                    label="Sites"
                    options={centers}
                    selectedItems={selectedCenters}
                    onToggle={handleCenterToggle}
                    loading={centersLoading}
                    error={centersError}
                    emptyMessage="No sites available"
                />
                <div className="control-row" style={{ marginTop: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>Harmonize</span>
                        <div className="toggle-switch">
                            <input
                                ref={harmonizeToggleRef}
                                type="checkbox"
                                className="toggle-checkbox"
                                checked={harmonize}
                                onChange={(e) => {
                                    setHarmonize(e.target.checked);
                                    console.log('Harmonize:', e.target.checked);
                                }}
                            />
                            <label
                                className="toggle-label"
                                onClick={() => {
                                    if (harmonizeToggleRef.current) {
                                        harmonizeToggleRef.current.click();
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="control-section">
                <h3>Select Patients</h3>
                <MultiSelectDropdown
                    label="Patients"
                    options={patientsWithSiteInfo}
                    selectedItems={selectedPatients}
                    onToggle={handlePatientToggle}
                    loading={patientsLoading}
                    error={patientsError}
                    groupBy={true}
                    getGroupKey={(option) => option.siteId || 'unknown'}
                    getGroupLabel={(groupKey) => {
                        const center = centers.find(c => c.id === groupKey);
                        return center?.label || groupKey;
                    }}
                    emptyMessage="No patients available"
                    collapsibleGroups={true}
                />
            </section>

            <section className="control-section">
                <h3>Select ROIs</h3>
                <MultiSelectDropdown
                    label="ROIs"
                    options={regions}
                    selectedItems={selectedROIs}
                    onToggle={handleROIToggle}
                    loading={regionsLoading}
                    error={regionsError}
                    groupBy={true}
                    getGroupKey={(option) => {
                        const roiName = option.label || option.id || option;
                        const lowerName = roiName.toLowerCase();
                        if (lowerName.startsWith('left')) return 'left';
                        if (lowerName.startsWith('right')) return 'right';
                        return 'other';
                    }}
                    getGroupLabel={(groupKey) => {
                        if (groupKey === 'left') return 'Left';
                        if (groupKey === 'right') return 'Right';
                        return 'Other';
                    }}
                    emptyMessage="No ROIs available"
                    collapsibleGroups={true}
                    formatOptionLabel={(label) => {
                        // Strip "Left " or "Right " from the beginning of the label
                        return label.replace(/^(Left|Right)\s+/i, '');
                    }}
                />
            </section>

            <section className="control-section">
                <h3>Select Features</h3>
                <MultiSelectDropdown
                    label="Features"
                    options={features}
                    selectedItems={selectedFeatures}
                    onToggle={handleFeatureToggle}
                    loading={featuresLoading}
                    error={featuresError}
                    emptyMessage="No features available"
                />
                <h3 style={{ marginTop: '20px' }}>Select Montage</h3>
                <div className="control-row">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>CAR</span>
                        <div className="normative-toggle-container">
                            <div className="normative-toggle-switch">
                                <input
                                    ref={montageToggleRef}
                                    type="checkbox"
                                    className="normative-toggle-checkbox"
                                    checked={montageType === 'Bipolar'}
                                    onChange={(e) => {
                                        const newType = e.target.checked ? 'Bipolar' : 'CAR';
                                        setMontageType(newType);
                                        console.log('Montage type:', newType);
                                    }}
                                />
                                <span
                                    className={`normative-toggle-slider ${montageType === 'Bipolar' ? 'checked' : ''}`}
                                    onClick={() => {
                                        if (montageToggleRef.current) {
                                            montageToggleRef.current.click();
                                        }
                                    }}
                                >
                                    <span className={`normative-toggle-slider-before ${montageType === 'Bipolar' ? 'checked' : ''}`}></span>
                                </span>
                            </div>
                        </div>
                        <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>Bipolar</span>
                    </div>
                </div>
                <div className="control-row" style={{ marginTop: '20px' }}>
                    <button
                        className="download-button"
                        disabled={selectedPatients.length === 0 || selectedFeatures.length === 0 || downloadLoading}
                        onClick={handleDownloadNormativeData}
                    >
                        <Download size={16} />
                        <span>{downloadLoading ? 'Preparing...' : 'Download JSON'}</span>
                    </button>
                </div>
            </section>
        </>
    );
};
